/**
 * LabelManagement - Upload and manage feed bag label templates.
 * Labels are stored in Django media storage (DigitalOcean Spaces in production).
 * Brother QL-1110NWB format: 4.07" x 6.4" die-cut labels.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { labelAPI } from '../../services/millAPI';
import MobileScrollTable from '../../components/common/MobileScrollTable';
import './Mill.css';

interface LabelTemplate {
  id: number;
  name: string;
  form: string;
  form_display: string;
  net_weight: string;
  net_weight_lbs: number;
  feed_formula_id: number | null;
  feed_formula_name: string | null;
  sku: string | null;
  upc_code: string | null;
  label_image_url: string | null;
  has_image: boolean;
  label_file_name: string;
  milled_date_x: number;
  milled_date_y: number;
  batch_number_x: number;
  batch_number_y: number;
  font_size: number;
}

interface Formula {
  id: number;
  name: string;
}

const LabelManagement: React.FC = () => {
  const [templates, setTemplates] = useState<LabelTemplate[]>([]);
  const [formulas, setFormulas] = useState<Formula[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Upload form state
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadFormulaId, setUploadFormulaId] = useState('');
  const [uploadForm, setUploadForm] = useState('MASH');
  const [uploadNetWeight, setUploadNetWeight] = useState('40 LB');
  const [uploadNetWeightLbs, setUploadNetWeightLbs] = useState('40');
  const [uploadSku, setUploadSku] = useState('');
  const [uploadUpc, setUploadUpc] = useState('');

  // Edit state
  const [editingId, setEditingId] = useState<number | null>(null);

  // Preview state
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewName, setPreviewName] = useState('');
  const [loadingPreview, setLoadingPreview] = useState<number | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 4000);
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [templatesRes, formulasRes] = await Promise.all([
        labelAPI.getTemplates(),
        labelAPI.getFormulas(),
      ]);
      setTemplates(templatesRes.data.results || []);
      setFormulas(formulasRes.data.results || []);
    } catch (err: any) {
      setError('Failed to load label templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadFile(file);

    // Auto-fill name from filename
    if (!uploadName) {
      setUploadName(file.name.replace(/\.[^.]+$/, ''));
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (ev) => {
      setUploadPreview(ev.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadPreview(null);
    setUploadName('');
    setUploadFormulaId('');
    setUploadForm('MASH');
    setUploadNetWeight('40 LB');
    setUploadNetWeightLbs('40');
    setUploadSku('');
    setUploadUpc('');
    setEditingId(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async () => {
    if (!editingId && !uploadFile) {
      setError('Please select a label image');
      return;
    }

    try {
      setUploading(true);
      setError('');

      const formData = new FormData();
      if (uploadFile) {
        formData.append('label_image', uploadFile);
      }
      if (uploadName) formData.append('name', uploadName);
      if (uploadFormulaId) formData.append('feed_formula_id', uploadFormulaId);
      formData.append('form', uploadForm);
      formData.append('net_weight', uploadNetWeight);
      formData.append('net_weight_lbs', uploadNetWeightLbs);
      if (uploadSku) formData.append('sku', uploadSku);
      if (uploadUpc) formData.append('upc_code', uploadUpc);

      if (editingId) {
        await labelAPI.updateTemplate(editingId, formData);
        showSuccess('Label template updated');
      } else {
        await labelAPI.createTemplate(formData);
        showSuccess('Label template uploaded');
      }

      resetUploadForm();
      setShowUploadForm(false);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save label template');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (template: LabelTemplate) => {
    setEditingId(template.id);
    setUploadName(template.name);
    setUploadFormulaId(template.feed_formula_id?.toString() || '');
    setUploadForm(template.form);
    setUploadNetWeight(template.net_weight);
    setUploadNetWeightLbs(template.net_weight_lbs.toString());
    setUploadSku(template.sku || '');
    setUploadUpc(template.upc_code || '');
    setUploadFile(null);
    setUploadPreview(template.label_image_url);
    setShowUploadForm(true);
  };

  const handleDelete = async (template: LabelTemplate) => {
    if (!window.confirm(`Delete label template "${template.name}"?`)) return;

    try {
      await labelAPI.deleteTemplate(template.id);
      showSuccess('Label template removed');
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete template');
    }
  };

  const handlePreview = async (template: LabelTemplate) => {
    try {
      setLoadingPreview(template.id);
      const res = await labelAPI.previewTemplate(template.id);
      setPreviewImage(res.data.image);
      setPreviewName(template.name);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate preview');
    } finally {
      setLoadingPreview(null);
    }
  };

  if (loading) {
    return (
      <div className="mill-page">
        <div className="mill-loading">
          <div className="mill-loading-spinner"></div>
          <p>Loading label templates...</p>
        </div>
      </div>
    );
  }

  const templatesWithImage = templates.filter(t => t.has_image).length;
  const templatesMissing = templates.filter(t => !t.has_image).length;

  return (
    <div className="mill-page">
      {/* Success Toast */}
      {successMessage && (
        <div className="mill-toast mill-toast-success">
          <span className="toast-icon">&#10003;</span>
          {successMessage}
        </div>
      )}

      {/* Header */}
      <div className="mill-page-header">
        <div>
          <h1>Label Management</h1>
          <p className="mill-page-subtitle">
            Upload and manage feed bag label images for Brother QL-1110NWB (4.07" x 6.4")
          </p>
        </div>
        <div className="header-actions">
          <button
            className="mill-btn mill-btn-primary"
            onClick={() => {
              resetUploadForm();
              setShowUploadForm(!showUploadForm);
            }}
          >
            {showUploadForm ? 'Cancel' : 'Upload New Label'}
          </button>
        </div>
      </div>

      {error && <div className="mill-alert mill-alert-error">{error}</div>}

      {/* Stats */}
      <div className="mill-status-summary">
        <div className="mill-summary-card">
          <span className="mill-summary-count">{templates.length}</span>
          <span className="mill-summary-label">Total Templates</span>
        </div>
        <div className="mill-summary-card success">
          <span className="mill-summary-count">{templatesWithImage}</span>
          <span className="mill-summary-label">With Image</span>
        </div>
        {templatesMissing > 0 && (
          <div className="mill-summary-card warning">
            <span className="mill-summary-count">{templatesMissing}</span>
            <span className="mill-summary-label">Missing Image</span>
          </div>
        )}
      </div>

      {/* Upload / Edit Form */}
      {showUploadForm && (
        <div className="mill-section label-upload-form">
          <h2 className="mill-section-title">
            {editingId ? 'Edit Label Template' : 'Upload New Label'}
          </h2>
          <div className="label-form-grid">
            <div className="label-form-left">
              <div className="label-form-group">
                <label>Label Image *</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/bmp,image/tiff"
                  onChange={handleFileSelect}
                  className="label-file-input"
                />
                <span className="label-form-hint">JPG, PNG, BMP, or TIFF. Recommended: 1799x1202px</span>
              </div>

              <div className="label-form-group">
                <label>Template Name</label>
                <input
                  type="text"
                  value={uploadName}
                  onChange={e => setUploadName(e.target.value)}
                  placeholder="e.g., All Flock Layer Mash 40#"
                  className="mill-input"
                />
              </div>

              <div className="label-form-row">
                <div className="label-form-group">
                  <label>Formula</label>
                  <select
                    value={uploadFormulaId}
                    onChange={e => setUploadFormulaId(e.target.value)}
                    className="mill-select"
                  >
                    <option value="">-- No Formula --</option>
                    {formulas.map(f => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </div>

                <div className="label-form-group">
                  <label>Feed Form</label>
                  <select
                    value={uploadForm}
                    onChange={e => setUploadForm(e.target.value)}
                    className="mill-select"
                  >
                    <option value="MASH">Mash</option>
                    <option value="PELLET">Pellet</option>
                  </select>
                </div>
              </div>

              <div className="label-form-row">
                <div className="label-form-group">
                  <label>Net Weight Display</label>
                  <input
                    type="text"
                    value={uploadNetWeight}
                    onChange={e => setUploadNetWeight(e.target.value)}
                    placeholder="40 LB"
                    className="mill-input"
                  />
                </div>

                <div className="label-form-group">
                  <label>Weight (lbs)</label>
                  <input
                    type="number"
                    value={uploadNetWeightLbs}
                    onChange={e => setUploadNetWeightLbs(e.target.value)}
                    placeholder="40"
                    className="mill-input"
                  />
                </div>
              </div>

              <div className="label-form-row">
                <div className="label-form-group">
                  <label>SKU</label>
                  <input
                    type="text"
                    value={uploadSku}
                    onChange={e => setUploadSku(e.target.value)}
                    placeholder="FST-POU-ALL_FLOCK-M-40"
                    className="mill-input"
                  />
                </div>

                <div className="label-form-group">
                  <label>UPC Code</label>
                  <input
                    type="text"
                    value={uploadUpc}
                    onChange={e => setUploadUpc(e.target.value)}
                    placeholder="8 67613 00040 3"
                    className="mill-input"
                  />
                </div>
              </div>

              <div className="label-form-actions">
                <button
                  className="mill-btn mill-btn-primary"
                  onClick={handleUpload}
                  disabled={uploading || (!editingId && !uploadFile)}
                >
                  {uploading ? 'Saving...' : editingId ? 'Update Template' : 'Upload Template'}
                </button>
                <button
                  className="mill-btn mill-btn-secondary"
                  onClick={() => {
                    resetUploadForm();
                    setShowUploadForm(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>

            <div className="label-form-right">
              {uploadPreview ? (
                <div className="label-preview-box">
                  <h3>Preview</h3>
                  <img
                    src={uploadPreview}
                    alt="Label preview"
                    className="label-preview-image"
                  />
                </div>
              ) : (
                <div className="label-preview-box label-preview-empty">
                  <p>Select an image to see preview</p>
                  <span className="label-preview-hint">4.07" x 6.4" landscape</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Templates Table */}
      <div className="mill-section">
        <h2 className="mill-section-title">Label Templates ({templates.length})</h2>

        {templates.length === 0 ? (
          <div className="mill-no-items">
            <p>No label templates found. Upload your first label image above.</p>
          </div>
        ) : (
          <MobileScrollTable>
            <table className="mill-table label-table">
              <thead>
                <tr>
                  <th>Preview</th>
                  <th>Name</th>
                  <th>Formula</th>
                  <th>Form</th>
                  <th>Weight</th>
                  <th>SKU</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map(template => (
                  <tr key={template.id} className={!template.has_image ? 'label-row-missing' : ''}>
                    <td className="label-thumb-cell">
                      {template.label_image_url ? (
                        <img
                          src={template.label_image_url}
                          alt={template.name}
                          className="label-thumb"
                          onClick={() => handlePreview(template)}
                        />
                      ) : (
                        <div className="label-thumb-placeholder">No Image</div>
                      )}
                    </td>
                    <td>
                      <strong>{template.name}</strong>
                      {template.label_file_name && (
                        <div className="label-filename">{template.label_file_name}</div>
                      )}
                    </td>
                    <td>{template.feed_formula_name || <span className="text-muted">Unlinked</span>}</td>
                    <td>
                      <span className={`feed-form-badge form-${template.form.toLowerCase()}`}>
                        {template.form_display}
                      </span>
                    </td>
                    <td>{template.net_weight}</td>
                    <td className="label-sku-cell">{template.sku || '—'}</td>
                    <td>
                      {template.has_image ? (
                        <span className="label-status-ok">Ready</span>
                      ) : (
                        <span className="label-status-missing">Needs Upload</span>
                      )}
                    </td>
                    <td className="label-actions-cell">
                      <button
                        className="mill-btn mill-btn-sm mill-btn-secondary"
                        onClick={() => handlePreview(template)}
                        disabled={!template.has_image || loadingPreview === template.id}
                      >
                        {loadingPreview === template.id ? '...' : 'Preview'}
                      </button>
                      <button
                        className="mill-btn mill-btn-sm mill-btn-primary"
                        onClick={() => handleEdit(template)}
                      >
                        Edit
                      </button>
                      <button
                        className="mill-btn mill-btn-sm mill-btn-danger"
                        onClick={() => handleDelete(template)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </MobileScrollTable>
        )}
      </div>

      {/* Label Preview Modal */}
      {previewImage && (
        <div className="label-preview-modal-overlay" onClick={() => setPreviewImage(null)}>
          <div className="label-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="label-preview-modal-header">
              <h3>{previewName}</h3>
              <button className="label-preview-close" onClick={() => setPreviewImage(null)}>
                &times;
              </button>
            </div>
            <div className="label-preview-modal-body">
              <img src={previewImage} alt="Label preview with overlay" className="label-preview-full" />
              <p className="label-preview-note">
                Preview shows sample milled date and batch number overlay
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LabelManagement;
